<template>
  <admin-layout :title="$t('edit_x', { x: $t('Tax Rate') })">
    <tec-form-section @submitted="update">
      <template #title>
        <div class="flex items-center">
          <Link class="text-indigo-400 hover:text-indigo-600" :href="route('tax_rates')">{{ $t('Tax Rates') }}</Link>
          <span class="text-indigo-400 font-medium mx-2">/</span>
          <img v-if="edit.photo" class="block w-6 h-6 rounded-full mr-2" :src="edit.photo" />
          {{ edit.name }}
        </div>
      </template>

      <template #description>{{ $t('Update the record by modifying the details in the form below') }}</template>

      <template #form>
        <trashed-message v-if="edit.deleted_at" class="mb-6" @restore="restore">
          {{ $t('This record has been deleted.') }}
        </trashed-message>

        <div class="col-span-full lg:col-span-4 grid gap-6">
          <text-input v-model="form.name" :error="$page.props.errors.name" :label="$t('Name')" />
          <text-input type="number" v-model="form.rate" :error="$page.props.errors.rate" :label="$t('Rate')" />
          <div class="col-span-6 sm:col-span-4 lg:col-span-4">
            <select-input v-model="form.type" :error="$page.props.errors.type" :label="$t('Type')">
              <option value="Percentage">{{ $t('Percentage') }}</option>
              <option value="Fixed">{{ $t('Fixed') }}</option>
            </select-input>
          </div>
        </div>
      </template>

      <template #actions>
        <div class="w-full flex items-center justify-between">
          <button
            type="button"
            @click="destroy"
            v-if="!edit.deleted_at"
            class="text-red-600 px-4 py-2 rounded border-2 border-transparent hover:border-gray-300 focus:outline-none focus:border-gray-300"
          >
            {{ $t('delete_x', { x: $t('Tax Rate') }) }}
          </button>
          <button
            v-else
            type="button"
            @click="destroyPermanently"
            class="text-red-600 px-4 py-2 rounded border-2 border-transparent hover:border-gray-300 focus:outline-none focus:border-gray-300"
          >
            {{ $t('delete_x', { x: $t('Permanently') }) }}
          </button>
          <div class="flex items-center">
            <tec-action-message :on="form.recentlySuccessful" class="mr-3">{{ $t('Saved.') }}</tec-action-message>
            <loading-button type="submit" :loading="form.processing" :disabled="form.processing">{{ $t('Save') }}</loading-button>
          </div>
        </div>
      </template>
    </tec-form-section>

    <!-- Delete User Confirmation Modal -->
    <Dialog
      max-width="md"
      :show="permanent"
      action-type="delete"
      title="Delete Role?"
      :close="closePermanentModal"
      :action="deleteRolePermanently"
      action-text="Delete Permanently"
      :content="`<p class='mb-2'>${$t('Are you sure you want to delete the record permanently?')}</p>
        <p>${$t('Once deleted, all of its resources and data will be permanently deleted.')}</p>`"
    />

    <!-- Delete Account Confirmation Modal -->
    <Dialog
      :show="confirm"
      :close="closeModal"
      :action="deleteTaxRate"
      action-type="delete"
      title="Delete Tax Rate?"
      action-text="Delete Tax Rate"
      :content="$t('Are you sure you want to delete the record?')"
    />

    <!-- Restore Account Confirmation Modal -->
    <Dialog
      :show="restoreConf"
      :action="restoreTaxRate"
      title="Restore Tax Rate!"
      :close="closeRestoreModal"
      action-text="Restore Tax Rate"
      :content="$t('Are you sure you want to restore the record?')"
    />
  </admin-layout>
</template>

<script>
import { Link } from '@inertiajs/inertia-vue3';

import Dialog from '@/Shared/Dialog';
import TextInput from '@/Shared/TextInput';
import FileInput from '@/Shared/FileInput';
import SelectInput from '@/Shared/SelectInput';
import AdminLayout from '@/Layouts/AdminLayout';
import LoadingButton from '@/Shared/LoadingButton';
import TecDialogModal from '@/Jetstream/DialogModal';
import TrashedMessage from '@/Shared/TrashedMessage';
import TecFormSection from '@/Jetstream/FormSection';
import TecDangerButton from '@/Jetstream/DangerButton';
import TecSectionTitle from '@/Jetstream/SectionTitle';
import TecActionMessage from '@/Jetstream/ActionMessage';
import TecSecondaryButton from '@/Jetstream/SecondaryButton';

export default {
  props: ['edit'],

  components: {
    Link,
    Dialog,
    FileInput,
    TextInput,
    AdminLayout,
    SelectInput,
    LoadingButton,
    TecDialogModal,
    TecFormSection,
    TecDangerButton,
    TrashedMessage,
    TecSectionTitle,
    TecActionMessage,
    TecSecondaryButton,
  },

  data() {
    return {
      confirm: false,
      permanent: false,
      restoreConf: false,
      form: this.$inertia.form({
        _method: 'put',
        name: this.edit.name,
        type: this.edit.type,
        rate: Number(this.edit.rate).toString(),
      }),
    };
  },

  methods: {
    update() {
      this.form.post(this.route('tax_rates.update', this.edit.id));
    },
    destroy() {
      this.confirm = true;
    },
    deleteTaxRate() {
      this.form.delete(route('tax_rates.destroy', this.edit.id), {
        onSuccess: () => this.closeModal(),
      });
    },
    closeModal() {
      this.confirm = false;
    },
    restore() {
      this.restoreConf = true;
    },
    restoreTaxRate() {
      this.$inertia.put(this.route('tax_rates.restore', this.edit.id), {
        onSuccess: () => (this.restoreConf = false),
      });
    },
    closeRestoreModal() {
      this.restoreConf = false;
    },
    deletePermanently() {
      this.permanent = true;
    },
    deleteRolePermanently() {
      this.form.delete(route('roles.destroy.permanently', this.edit.id), {
        onSuccess: () => this.closeModal(),
      });
    },
    closePermanentModal() {
      this.permanent = false;
    },
  },
};
</script>
